import React, { Component } from "react";
import { connect } from "react-redux";
import { fetchCategoryNavigations } from "../../store/actions/categoryNavigation";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { css, StyleSheet } from "aphrodite";
import { PUBLIC_URL } from "../../config";
import LazyImage from "./LazyImage";
import { generateAltText } from "../../helpers/seo";

class CategoryNavigation extends Component {
  componentDidMount() {
    this.props.fetchCategoryNavigations();
  }

  renderCategoryIcon = (nav) => {
    // Priority: iconImage > icon > default
    if (nav.iconImage) {
      return (
        <React.Fragment>
          <LazyImage
            src={PUBLIC_URL(nav.iconImage)}
            alt={generateAltText('icon', nav, `${nav.title} kategorisi ikonu`)}
            title={`${nav.title} - Botaniksan'da keşfedin`}
            className="category-nav-icon-image"
            placeholderColor="transparent"
            loading="eager"
            onError={(e) => {
              // Fallback to FontAwesome icon if image fails to load
              e.target.style.display = "none";
              const fallbackIcon =
                e.target.parentNode.querySelector(".fallback-icon");
              if (fallbackIcon) {
                fallbackIcon.style.display = "flex";
              }
            }}
          />
          <FontAwesomeIcon
            icon={nav.icon ? nav.icon.split(" ") : ["fas", "tags"]}
            className={css(styles.iconFont) + " fallback-icon"}
            style={{ display: "none" }}
          />
        </React.Fragment>
      );
    } else if (nav.icon) {
      return (
        <FontAwesomeIcon
          icon={nav.icon.split(" ")}
          className={css(styles.iconFont)}
        />
      );
    } else {
      return (
        <FontAwesomeIcon
          icon={["fas", "tags"]}
          className={css(styles.iconFont)}
        />
      );
    }
  };

  renderCategoryItem = (nav, index) => {
    const style = nav.style || {};
    const bgColor = style.bgColor || "#ff6b35";
    const textColor = style.textColor || "#2c3e50";
    const hoverColor = style.hoverColor || "#ff8a65";
    
    // Create unique style ID for this nav item
    const uniqueId = `nav-item-${nav.id || index}`;
    
    // Dynamic hover styles
    const hoverStyles = `
      .${uniqueId}:hover .icon-circle {
        background-color: ${hoverColor} !important;
        transform: scale(1.05);
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
      }
      .${uniqueId}:hover .category-title {
        color: ${hoverColor} !important;
      }
    `;

    if (nav.customLink) {
      return (
        <React.Fragment key={index}>
          <style>{hoverStyles}</style>
          <a
            href={nav.customLink}
            className={css(styles.categoryItem) + ` ${uniqueId}`}
            target="_blank"
            rel="noopener noreferrer"
            title={`${nav.title} - Harici bağlantı`}
            aria-label={`${nav.title} kategorisini dış bağlantıda aç`}
          >
            <div
              className={css(styles.iconCircle) + " icon-circle"}
              style={{ backgroundColor: bgColor }}
            >
              {this.renderCategoryIcon(nav)}
            </div>
            <span 
              className={css(styles.categoryTitle) + " category-title"}
              style={{ color: textColor }}
            >
              {nav.title}
            </span>
          </a>
        </React.Fragment>
      );
    } else {
      // Always link to the category navigation page to show unique products
      return (
        <React.Fragment key={index}>
          <style>{hoverStyles}</style>
          <Link
            to={`/kategori-koleksiyonu/${nav.id}`}
            className={css(styles.categoryItem) + ` ${uniqueId}`}
            title={`${nav.title} kategorisi - Botaniksan'da göz atın`}
            aria-label={`${nav.title} kategorisindeki ürünleri görüntüle`}
          >
            <div
              className={css(styles.iconCircle) + " icon-circle"}
              style={{ backgroundColor: bgColor }}
            >
              {this.renderCategoryIcon(nav)}
            </div>
            <span 
              className={css(styles.categoryTitle) + " category-title"}
              style={{ color: textColor }}
            >
              {nav.title}
            </span>
          </Link>
        </React.Fragment>
      );
    }
  };

  render() {
    const { categoryNavigation } = this.props;
    const { categoryNavigations, loading } = categoryNavigation;

    if (loading || !categoryNavigations || categoryNavigations.length === 0) {
      return null;
    }

    return (
      <div className={css(styles.container)}>
        <style>{`
          .category-nav-icon-image {
            width: 40px !important;
            height: 40px !important;
            object-fit: cover !important;
            border-radius: 50% !important;
          }
          @media (max-width: 768px) {
            .category-nav-icon-image {
              width: 32px !important;
              height: 32px !important;
            }
          }
        `}</style>
        <div className="container">
          <div className={css(styles.categoriesWrapper)}>
            <div className={css(styles.categoriesScrollable)}>
              {categoryNavigations.map((nav, index) =>
                this.renderCategoryItem(nav, index)
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = ({ categoryNavigation }) => {
  return { categoryNavigation };
};

const mapDispatchToProps = {
  fetchCategoryNavigations,
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: "#f8f9fa",
    padding: "20px 0",
    borderBottom: "1px solid #e8e9ea",
    "@media (max-width: 768px)": {
      padding: "15px 0",
    },
  },
  categoriesWrapper: {
    position: "relative",
  },
  categoriesScrollable: {
    display: "flex",
    gap: "20px",
    overflowX: "auto",
    padding: "10px 0",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "::-webkit-scrollbar": {
      display: "none",
    },
    "@media (max-width: 768px)": {
      gap: "15px",
      paddingLeft: "15px",
      paddingRight: "15px",
    },
  },
  categoryItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textDecoration: "none",
    minWidth: "80px",
    maxWidth: "100px",
    transition: "all 0.3s ease",
    ":hover": {
      textDecoration: "none",
      transform: "translateY(-3px)",
    },
    "@media (max-width: 768px)": {
      minWidth: "70px",
      maxWidth: "85px",
    },
  },
  iconCircle: {
    width: "64px",
    height: "64px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "8px",
    boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
    transition: "all 0.3s ease",
    position: "relative",
    overflow: "hidden",
    "@media (max-width: 768px)": {
      width: "56px",
      height: "56px",
      marginBottom: "6px",
    },
  },
  iconFont: {
    fontSize: "24px",
    color: "white",
    "@media (max-width: 768px)": {
      fontSize: "20px",
    },
  },
  categoryTitle: {
    fontSize: "12px",
    fontWeight: "500",
    textAlign: "center",
    lineHeight: "1.3",
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    transition: "color 0.3s ease",
    "@media (max-width: 768px)": {
      fontSize: "11px",
      maxWidth: "85px",
    },
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(CategoryNavigation);



// WEBPACK FOOTER //
// ./src/components/shared/CategoryNavigation.js